import type { ListingTemplateProduct } from "@/lib/listing-templates";
import { Stars, TemplatePoweredFooter } from "./shared";
import { imageAt, brandOf } from "./template-helpers";

/**
 * Template 4 — Product Focus.
 * Image-led with large product imagery, short copy, prominent key features and
 * compact specifications. For when visuals matter more than long descriptions.
 */
export default function ProductFocusTemplate({ product }: { product: ListingTemplateProduct }) {
  const hero = imageAt(product, 0);
  const brand = brandOf(product);
  const bandImages = product.images.slice(1, 4);
  const glance = [
    { label: "Material", value: product.material },
    { label: "Weight", value: product.weight },
    { label: "Dimensions", value: product.dimensions },
    { label: "Warranty", value: product.warranty },
  ].filter((item) => item.value);

  const supportItems = [
    { icon: "ri-truck-line", ...product.shipping },
    { icon: "ri-refresh-line", ...product.returns },
    { icon: "ri-customer-service-2-line", ...product.support },
  ].filter((section) => section.lines.length > 0);

  return (
    <div className="bg-white text-slate-900 font-sans">
      {/* Primary product image */}
      {hero && (
        <div className="relative h-[340px] md:h-[520px] bg-slate-900">
          <img
            src={hero.src}
            alt={hero.alt}
            data-testid="template-hero-image"
            className="w-full h-full object-contain object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/15" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-10 text-white">
            <div className="max-w-5xl mx-auto">
              <div className="rounded-xl bg-black/45 backdrop-blur-sm px-4 py-3 md:px-6 md:py-5 inline-block max-w-3xl">
                {brand && (
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/85">{brand}</p>
                )}
                <h1 className="text-xl md:text-4xl font-semibold leading-tight mt-0.5 break-words">
                  {product.title}
                </h1>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xl md:text-3xl font-bold">{product.price}</span>
                  {product.wasPrice && <span className="text-white/65 line-through">{product.wasPrice}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-10">
        {/* Short intro */}
        <div className="max-w-2xl">
          <Stars score={product.rating.score} count={product.rating.count} />
          <p className="text-lg text-slate-600 leading-relaxed mt-3">{product.shortDescription}</p>
        </div>

        {/* Key features as bold chips */}
        {product.keyFeatures.length > 0 && (
          <section className="mt-10">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">Key features</h2>
            <div className="flex flex-wrap gap-3">
              {product.keyFeatures.map((feature) => (
                <span key={feature} className="px-4 py-2 bg-teal-50 text-teal-800 rounded-full text-sm font-medium">
                  {feature}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Image band */}
        {bandImages.length > 0 && (
          <section className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {bandImages.map((img) => (
              <img
                key={img.src}
                src={img.src}
                alt={img.alt}
                className="rounded-xl object-cover object-top w-full aspect-square"
              />
            ))}
          </section>
        )}

        {/* Compact specs */}
        {glance.length > 0 && (
          <section className="mt-12">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">At a glance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {glance.map((item) => (
                <div key={item.label} className="rounded-xl border border-slate-100 p-4">
                  <p className="text-xs text-slate-400">{item.label}</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1 leading-snug break-words">{item.value}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA band */}
        <section className="mt-12 rounded-2xl bg-teal-600 text-white px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-xl font-semibold">Ready to order?</h2>
            {product.shipping.lines[0] && (
              <p className="text-teal-50 text-sm mt-1">{product.shipping.lines[0]}</p>
            )}
          </div>
          <button className="px-8 py-3 bg-white text-teal-700 font-semibold rounded-full whitespace-nowrap">
            Add to cart
          </button>
        </section>

        {/* Shipping / support inline */}
        {supportItems.length > 0 && (
          <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            {supportItems.map((section) => (
              <div key={section.title} className="flex gap-3">
                <i className={`${section.icon} text-teal-600 text-xl`} />
                <div>
                  <h3 className="font-semibold">{section.title}</h3>
                  <p className="text-slate-500 mt-1 leading-relaxed break-words">
                    {section.lines.join(" · ")}
                  </p>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <TemplatePoweredFooter
            testId="product-focus-footer"
            left={brand ?? "Store"}
            sku={product.sku}
            className="text-slate-400"
            centerClassName="text-center text-slate-300"
          />
        </div>
      </footer>
    </div>
  );
}